<script setup lang="ts">
import { computed, ref, onMounted, defineExpose } from 'vue'
import {
  UIDrawer,
  UIDrawerContent,
  UISelect,
  UIEmpty,
  UIDivider,
  UITag,
  UICollapse,
  UICollapseItem,
  UITextSmMedium,
  UITextSmRegular,
  UIAvatar,
  UIButton,
} from '@gohighlevel/ghl-ui'
import {
  UserCircleIcon,
  EyeIcon,
  LinkExternal02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { startCase, debounce, uniqBy } from 'lodash'
import { getProfileColor, getInitials, getEmailDate } from 'common/utils/helper'
import { contactInfoKeys } from 'common/utils/const_helper'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  service: {
    type: Object,
  },
  state: {
    type: Object,
  },
  locationId: {
    type: String,
  },
  to: {
    type: String,
    default: 'body'
  }
})
const emit = defineEmits(['update:close', 'update:select'])

// contact variables
const selectedContact = ref('')
const contacts: any = ref([])
const loading = ref(false)
const totalContacts = ref(0)
const searchText = ref('')
const page = ref(0)
const initialList = ref([])
// select contact details
const selectedContactDetails = ref({})

// contact info
const contactInfo = computed(() => {
  if (selectedContactDetails.value) {
    const data = Object.entries(selectedContactDetails.value).filter(
      ([key, val]) => contactInfoKeys.includes(key)
    )
    return Object.fromEntries(data)
  }
  return {}
})

// custom fields
const customFields = computed(() => {
  return selectedContactDetails.value?.custom_fields?.map((item: any) => {
    const key = props.state?.customFieldsLocation?.filter(
      (el: any) => el?.id === item.id
    )[0]?.name
    return {
      key,
      value:
        item['field_value_string'] ??
        item['field_value_number'] ??
        item['field_value_array']?.join(', '),
    }
  })
})

const handleClose = async (val: any) => {
  if (!searchText.value) return
  if (!val) {
    // initialize contacts list after close (if search happened)
    setTimeout(() => {
      searchText.value = ''
      // include current selection
      const curSelection = {
        label: selectedContactDetails.value?.contact_name,
        value: selectedContact.value,
      }
      contacts.value = uniqBy([curSelection, ...initialList.value], 'value')
    }, 500)
  }
}

const fetchContacts = async (query: string = '', loadMore: boolean = false) => {
  try {
    loading.value = true
    const { data } = await props?.service.contactSearch({
      locationId: props.locationId,
      query,
      tag: '',
      assignedTo: props.state.enableAssignToData
        ? props.state.userId ?? ''
        : '',
      page: page.value / 20 + 1,
    })
    const { hits, total } = data.hits

    const newList = hits.map((item: any) => ({
      label: startCase(item?._source?.contact_name),
      value: item?._id,
      data: item?._source,
    }))

    if (loadMore) contacts.value = [...contacts.value, ...newList]
    else contacts.value = newList
    loading.value = false
    if (total.value > totalContacts.value) totalContacts.value = total.value
  } catch (error) {
    console.log(error)
    loading.value = false
  }
}

const handleSearch = debounce(async (val: string) => {
  searchText.value = val
  page.value = 0
  await fetchContacts(val)
}, 500)

const handleScroll = async (e: any) => {
  if (contacts.value.length >= totalContacts.value) return
  const currentTarget = e.currentTarget as HTMLElement
  if (
    totalContacts.value > 0 &&
    currentTarget.scrollTop + currentTarget.offsetHeight >=
      currentTarget.scrollHeight - 10
  ) {
    page.value = page.value + 1
    await fetchContacts(searchText.value, true)
  }
}

const handleSelect = (val: string) => {
  selectedContact.value = val
  updateSelectedDetails()
  emit('update:select', selectedContactDetails.value)
}

const handleRedirect = () => {
  const url = `${
    props.state.parentBaseUrl ?? window?.location?.ancestorOrigins[0]
  }/location/${props.locationId}/customers/detail/${selectedContact.value}`
  window.open(url, '_blank')
}

const updateSelectedDetails = () => {
  let data = contacts?.value?.filter(
    (val: any) => val.value === selectedContact.value
  )[0]?.data

  // these values are capitalized on sent email from contacts side
  const capitalizedKeys = [
    'contact_name',
    'full_name',
    'first_name',
    'last_name',
    'company_name',
    'city',
  ]

  for (const el in data) {
    // convert number to email format
    if (el === 'date_of_birth')
      data[el] = data[el] ? getEmailDate(data[el]) : ''
    if (capitalizedKeys.includes(el)) data[el] = startCase(data[el])
  }

  selectedContactDetails.value = data
}

onMounted(async () => {
  await fetchContacts()
  initialList.value = contacts.value
  handleSelect(contacts.value[0]?.value)
})
</script>

<template>
  <UIDrawer
    id="pg-email-builder-preview__contact-preview-drawer"
    :show="show"
    @update:show="emit('update:close')"
    :width="512"
    :to="to"
  >
    <UIDrawerContent
      id="pg-email-builder-preview__contact-preview-drawer-content"
      title="Preview contact info"
      class="p-0 fonts-fallback"
    >
      <UISelect
        type="avatar"
        :placeholder="$t('common.searchForAContact')"
        :options="contacts"
        :value="selectedContact"
        :loading="loading"
        :searchText="searchText"
        :reset-menu-on-options-change="false"
        @search="handleSearch"
        @update:value="handleSelect"
        @update:show="handleClose"
        @scroll="handleScroll"
        filterable
        remote
        class="mb-4"
        id="pg-email-builder-preview__select_contact"
      />
      <UIDivider />
      <UIEmpty
        v-if="!selectedContact"
        id="pg-email-builder-preview__empty_contact"
        :title="$t('common.noContactsPickedYet')"
        :description="$t('common.emptyContactDescription')"
        :icon="UserCircleIcon"
      />
      <div v-else>
        <div class="padding-4 bg-primary-50 margin-b-4 rounded-lg">
          <UITag class="gap-2 margin-b-4" type="info"
            ><EyeIcon class="w-5 h-5 mr-1 inline" />{{
              $t('common.previewingAs')
            }}
          </UITag>
          <div class="flex justify-between">
            <div class="flex items-center gap-3">
              <UIAvatar
                :style="{
                  backgroundColor: getProfileColor(contactInfo?.contact_name),
                }"
                >{{ getInitials(contactInfo?.contact_name) }}</UIAvatar
              >
              <UITextSmMedium> {{ contactInfo?.contact_name }}</UITextSmMedium>
            </div>
            <UIButton @click="handleRedirect" text
              ><LinkExternal02Icon
                class="text-primary-700 w-4 h-5 cursor-pointer"
            /></UIButton>
          </div>
        </div>
        <UICollapse defaultExpandedNames="contact-info">
          <UICollapseItem name="contact-info" :title="$t('common.contactInfo')">
            <div v-for="item in Object.keys(contactInfo ?? {})">
              <div class="grid grid-cols-2 gap-3 my-3">
                <UITextSmMedium
                  >{{ startCase(item.split('_').join(' ')) }}:</UITextSmMedium
                >
                <UITextSmRegular>
                  {{ contactInfo[item] ?? '' }}
                </UITextSmRegular>
              </div>
            </div>
          </UICollapseItem>
          <UICollapseItem
            name="custom-fields"
            :title="$t('common.customFields')"
            v-if="customFields?.length"
          >
            <div v-for="{ key, value } in customFields">
              <div class="grid grid-cols-2 gap-3 my-3" v-if="key">
                <UITextSmMedium>{{ startCase(key) }}:</UITextSmMedium>
                <UITextSmRegular>
                  {{ value }}
                </UITextSmRegular>
              </div>
            </div>
          </UICollapseItem>
        </UICollapse>
      </div>
    </UIDrawerContent>
  </UIDrawer>
</template>
<style>
.margin-b-4 {
  margin-bottom: 1rem;
}
.padding-4 {
  padding: 1rem;
}
.n-base-selection-label__render-label .n-base-selection-overlay {
  padding-left: 12px;
  padding-right: 26px;
}
#pg-email-builder-preview__contact-preview-drawer .v-binder-follower-content {
  transform: translateX(24px) translateY(119px) !important;
  transform-origin: left top !important;
}
</style>